/**
 * Admin-Dashboard – /admin
 *
 * Tabs:
 *   SERVER        – Server-Status/Auslastung (CPU/RAM/Disk, DB/Redis, PM2, aktive Sessions).
 *   NUTZER        – Alle registrierten Accounts inkl. Admins und gelöschte.
 *   BENACHRICHTIGUNGEN – Broadcasts + Scheine-Vergabe.
 *   STREAMER      – Partner-Onboarding (NT-STREAMER-ONBOARD-01): Twitch-Lookup, Anlegen,
 *                    Aktivieren/Deaktivieren.
 *   BRETT         – Das Schwarze Brett (NT-SCHWARZES-BRETT-01): Kuratier-Kanban.
 *
 * Der frühere BEWERBUNGEN-Tab (Alt-Alpha-Bewerbungssystem) ist entfernt (Chris-Feedback
 * 2026-07-30) — das Bewerbungssystem wurde bereits mit NT-ALPHA-REWORK-01 durch die
 * Self-Service-Registrierung ersetzt, `POST /alpha/apply` liefert seitdem 410 Gone. Die
 * Backend-Routen `GET/POST /admin/applications/...` bleiben bewusst aktiv (Alt-Bewerbungen
 * könnten noch in PENDING-Status existieren) — nur die tote Frontend-Ansicht ist entfernt.
 */

import { navigate } from '../router.js';
import { gameState } from '../state/gameState.js';
import { logoutUser } from '../services/auth.js';
import { initAdminUsers } from './admin/admin-users.js';
import { initAdminNotifications } from './admin/admin-notifications.js';
import { initAdminSystemStatus } from './admin/admin-system-status.js';
import { initAdminStreamers } from './admin/admin-streamers.js';
import { initAdminSuggestions } from './admin/admin-suggestions.js';

type ActiveTab = 'system' | 'users' | 'notifications' | 'streamers' | 'suggestions';

export function renderAdmin(): void {
  if (!gameState.isAuthenticated()) {
    navigate('/login');
    return;
  }

  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = `
    <main class="admin-page" style="padding:24px;max-width:1200px;margin:0 auto;">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">
        <p class="auth-brand" style="margin:0;">&#9654; NIGHTTRADE</p>
        <button id="admin-logout-btn" class="dash-nav-action">LOGOUT</button>
      </div>
      <h1 class="auth-title">Admin-Dashboard</h1>

      <nav style="display:flex;gap:0;margin-bottom:24px;border-bottom:1px solid #2a2a38;">
        <button
          id="tab-system"
          data-tab="system"
          style="padding:10px 20px;border:none;border-bottom:3px solid #52b788;
                 background:transparent;color:#52b788;cursor:pointer;
                 font-family:'Share Tech Mono',monospace;font-size:13px;letter-spacing:0.1em;">
          SERVER
        </button>
        <button
          id="tab-users"
          data-tab="users"
          style="padding:10px 20px;border:none;border-bottom:3px solid transparent;
                 background:transparent;color:#6b6b80;cursor:pointer;
                 font-family:'Share Tech Mono',monospace;font-size:13px;letter-spacing:0.1em;">
          NUTZER
        </button>
        <button
          id="tab-notifications"
          data-tab="notifications"
          style="padding:10px 20px;border:none;border-bottom:3px solid transparent;
                 background:transparent;color:#6b6b80;cursor:pointer;
                 font-family:'Share Tech Mono',monospace;font-size:13px;letter-spacing:0.1em;">
          BENACHRICHTIGUNGEN
        </button>
        <button
          id="tab-streamers"
          data-tab="streamers"
          style="padding:10px 20px;border:none;border-bottom:3px solid transparent;
                 background:transparent;color:#6b6b80;cursor:pointer;
                 font-family:'Share Tech Mono',monospace;font-size:13px;letter-spacing:0.1em;">
          STREAMER
        </button>
        <button
          id="tab-suggestions"
          data-tab="suggestions"
          style="padding:10px 20px;border:none;border-bottom:3px solid transparent;
                 background:transparent;color:#6b6b80;cursor:pointer;
                 font-family:'Share Tech Mono',monospace;font-size:13px;letter-spacing:0.1em;">
          BRETT
        </button>
      </nav>

      <div id="tab-content-system">
        <p style="color:#9090a8;">LADE SERVER-STATUS...</p>
      </div>
      <div id="tab-content-users" hidden>
        <p style="color:#9090a8;">KLICKE AUF DEN TAB.</p>
      </div>
      <div id="tab-content-notifications" hidden>
        <p style="color:#9090a8;">KLICKE AUF DEN TAB.</p>
      </div>
      <div id="tab-content-streamers" hidden>
        <p style="color:#9090a8;">KLICKE AUF DEN TAB.</p>
      </div>
      <div id="tab-content-suggestions" hidden>
        <p style="color:#9090a8;">KLICKE AUF DEN TAB.</p>
      </div>
    </main>
  `;

  document.querySelectorAll<HTMLButtonElement>('button[data-tab]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tab = btn.getAttribute('data-tab') as ActiveTab;
      switchTab(tab);
    });
  });

  document.getElementById('admin-logout-btn')?.addEventListener('click', () => {
    void logoutUser().then(() => { navigate('/login'); });
  });

  initAdminSystemStatus('tab-content-system');
}

function switchTab(tab: ActiveTab): void {
  const tabs: ActiveTab[] = ['system', 'users', 'notifications', 'streamers', 'suggestions'];

  tabs.forEach((t) => {
    const btn = document.getElementById(`tab-${t}`);
    const content = document.getElementById(`tab-content-${t}`);
    const isActive = t === tab;
    if (btn) {
      btn.style.borderBottomColor = isActive ? '#52b788' : 'transparent';
      btn.style.color = isActive ? '#52b788' : '#6b6b80';
    }
    if (content) content.hidden = !isActive;
  });

  if (tab === 'system') initAdminSystemStatus('tab-content-system');
  if (tab === 'users') initAdminUsers('tab-content-users');
  if (tab === 'notifications') initAdminNotifications('tab-content-notifications');
  if (tab === 'streamers') initAdminStreamers('tab-content-streamers');
  if (tab === 'suggestions') initAdminSuggestions('tab-content-suggestions');
}
